import {Component, computed, inject, OnInit, SecurityContext, signal} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {NgClass} from '@angular/common';
import {EventService} from '../../../../shared/services/event.service';
import {AlertService} from '../../../../shared/services/alert.service';
import {ConfirmDialogService} from '../../../../shared/services/confirm-dialog.service';
import {Messages} from '../../../../shared/constants/messages';
import {Event} from '../../../../shared/model/event';
import {GenericCardComponent} from '../../../../shared/components/generic-card/generic-card.component';
import {EventGameTableCardComponent} from './event-game-table-card/event-game-table-card.component';
import {GeoLocationService} from '../../services/geo-location.service';
import {DomSanitizer} from '@angular/platform-browser';
import {GameTableFormComponent} from '../../gametables/game-table-form/game-table-form.component';
import {GameTablesService} from '../../services/gametables.service';
import {GameTable} from '../../../../shared/model/game-table';
import {ApiResponse} from '../../../../shared/model/api-response';
import {Organization} from '../../../../shared/model/organization';
import {NavigationHistoryService} from '../../../../core/services/navigation-history.service';
import {OrganizationService} from '../../../../shared/services/organization.service';
import {PermissionService} from '../../../../core/services/permission.service';
import {ImageLightboxComponent} from '../../../../shared/components/image-lightbox/image-lightbox.component';
import {EventGdtListModalComponent} from './event-gdt-list-modal/event-gdt-list-modal.component';
import {GenericSearchSelectComponent} from '../../../../shared/components/generic-search-select/generic-search-select.component';
import {FormControl} from '@angular/forms';
import {OrgCapability, SystemPower} from '../../../../shared/model/permissions';
import {DateRangePipe} from '../../../../shared/pipes/date-range.pipe';
import {GameTableSessionsComponent} from '../../gametables/game-table-detail/game-table-sessions/game-table-sessions.component';

@Component({
  selector: 'app-event-detail',
  standalone: true,
  imports: [
    GenericCardComponent,
    NgClass,
    EventGameTableCardComponent,
    GameTableFormComponent,
    ImageLightboxComponent,
    RouterLink,
    GenericSearchSelectComponent,
    DateRangePipe,
    GameTableSessionsComponent
  ],
  templateUrl: './event-detail.component.html',
  styleUrl: './event-detail.component.css'
})
export class EventDetailComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private eventService: EventService = inject(EventService);
  private alertService: AlertService = inject(AlertService);
  private confirmDialogService: ConfirmDialogService = inject(ConfirmDialogService);
  protected geoLocationService: GeoLocationService = inject(GeoLocationService);
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private gameTablesService: GameTablesService = inject(GameTablesService);
  private navigationHistory: NavigationHistoryService = inject(NavigationHistoryService);
  private organizationService: OrganizationService = inject(OrganizationService);
  private permissionService: PermissionService = inject(PermissionService);

  private modalService = inject(NgbModal);

  protected event = signal<Event | undefined>(undefined);
  protected showCreateGdrTable = signal<boolean>(false);
  protected showCreateGdtTable = signal<boolean>(false);
  protected creatingTable = signal<boolean>(false);
  protected organizationWebsite = signal<string | undefined>(undefined);
  protected showAddInvitedOrganization = signal<boolean>(false);
  protected allActiveOrganizations = signal<Organization[]>([]);
  protected searchOrganizationControl = new FormControl<Organization | null>(null);

  private getEventAllOrganizations = computed<Organization[]>(() => {
    const ev = this.event();
    if (!ev) return [];
    const orgs: Organization[] = [];
    const userOrgs = this.permissionService.getCurrentUser()?.organizations || [];

    const addOrg = (baseOrg: Partial<Organization>) => {
      if (baseOrg.organizationActive !== false && !orgs.some(o => o.id === baseOrg.id)) {
        const userOrg = userOrgs.find(uo => uo.id === baseOrg.id);
        orgs.push({
          id: baseOrg.id!,
          organizationName: baseOrg.organizationName!,
          organizationLogo: baseOrg.organizationLogo,
          organizationActive: baseOrg.organizationActive ?? true,
          member: userOrg ? userOrg.member : false,
          moderator: userOrg ? userOrg.moderator : false,
          admin: userOrg ? userOrg.admin : false,
          owner: userOrg ? userOrg.owner : false
        });
      }
    };

    if (ev.organization) {
      addOrg(ev.organization);
    }
    if (ev.invitedOrganizations) {
      for (const inv of ev.invitedOrganizations) {
        addOrg(inv);
      }
    }

    return orgs;
  });

  protected eventOrganizations = computed<Organization[]>(() => {
    const ev = this.event();
    if (!ev?.organization) return [];
    return this.getEventAllOrganizations().filter(org => org.id === ev.organization?.id);
  });

  protected authorizedTableOrganizations = computed<Organization[]>(() => {
    return this.getEventAllOrganizations().filter(org =>
      this.permissionService.hasOrgCapability(org, OrgCapability.ORG_TABLE_CREATE) ||
      this.permissionService.hasSystemPower(SystemPower.MODERATOR) ||
      this.canManageEvent()
    );
  });

  protected availableOrganizations = computed<Organization[]>(() => {
    const ev = this.event();
    const all = this.allActiveOrganizations();
    if (!ev || !all) return [];

    return all.filter(org => {
      if (ev.organization?.id === org.id) return false;
      if (ev.invitedOrganizations?.some(inv => inv.id === org.id)) return false;
      return true;
    });
  });

  protected organizationDisplayFn = (org: Organization): string => org.organizationName;

  protected gdrTables = computed<GameTable[]>(() => {
    const ev = this.event();
    if (!ev || !ev.gameTables) return [];
    return ev.gameTables
      .filter(t => !t.tableType || t.tableType === 'GDR')
      .map(t => {
        if (!t.event) {
          return { ...t, event: ev };
        }
        return t;
      });
  });

  protected gdtTables = computed<GameTable[]>(() => {
    const ev = this.event();
    if (!ev || !ev.gameTables) return [];
    return ev.gameTables.filter(t => t.tableType === 'GDT');
  });

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.loadEvent(Number(idParam));
    } else {
      this.handleMissing();
    }
  }

  private fetchActiveOrganizations(): void {
    if (this.allActiveOrganizations().length > 0) {
      return;
    }
    this.organizationService.getAllOrganizations(false).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.allActiveOrganizations.set(response.data);
        }
      },
      error: () => {}
    });
  }

  onEdit() {
    if (!this.canManageEvent()) {
      return;
    }
    const ev = this.event();
    if (ev?.id) {
      this.router.navigate(['/events/edit', ev.id]);
    }
  }

  async onDelete() {
    if (!this.canManageEvent()) {
      return;
    }
    const ev = this.event();
    if (!ev?.id) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_EVENT_DELETE_TITLE,
      message: Messages.confirmEventDelete(ev.name),
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    this.eventService.deleteEvent(ev.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.router.navigate(['/events/mine']);
        }
      },
      error: () => {
        this.alertService.showError(Messages.EVENT_DELETE_ERROR);
      }
    });
  }

  goBack() {
    this.navigationHistory.goBack(['/events']);
  }

  protected canManageEvent(): boolean {
    const ev = this.event();
    return !!ev?.isCreator && ev?.organization?.organizationActive !== false;
  }

  protected canAddTable(): boolean {
    return this.authorizedTableOrganizations().length > 0;
  }

  protected toggleCreateGdrTable(): void {
    this.showCreateGdrTable.update((v) => !v);
    this.showCreateGdtTable.set(false);
  }

  protected toggleCreateGdtTable(): void {
    this.showCreateGdtTable.update((v) => !v);
    this.showCreateGdrTable.set(false);
  }

  protected onCreateTable(tableData: Partial<GameTable>, tableType: 'GDR' | 'GDT'): void {
    const ev = this.event();
    if (!ev?.id || this.creatingTable()) {
      return;
    }

    this.creatingTable.set(true);
    const payload: Partial<GameTable> = {
      ...tableData,
      tableType: tableType,
      organization: tableData.organization ?? ev.organization,
      event: tableData.event ?? ({id: ev.id} as Event)
    };
    this.gameTablesService.createTable(payload).subscribe({
      next: (response: ApiResponse<GameTable>) => {
        this.creatingTable.set(false);
        if (response.success && response.data) {
          const newTable = response.data;
          this.event.update(curr => {
            if (!curr) return curr;
            const tables = curr.gameTables ? [...curr.gameTables] : [];
            if (!tables.some(t => t.id === newTable.id)) {
              tables.push(newTable);
            }
            return { ...curr, gameTables: tables };
          });

          this.alertService.showSuccess(Messages.TABLE_CREATED_SUCCESS);
          this.showCreateGdrTable.set(false);
          this.showCreateGdtTable.set(false);
          this.loadEvent(ev.id!);
        }
      },
      error: (err) => {
        this.creatingTable.set(false);
        if (!err?.error?.message) {
          this.alertService.showError(Messages.TABLE_SAVE_ERROR);
        }
      }
    });
  }

  protected reloadEvent(): void {
    const ev = this.event();
    if (ev?.id) {
      this.loadEvent(ev.id);
    }
  }

  protected openGdtListModal(table: GameTable): void {
    const modalRef = this.modalService.open(EventGdtListModalComponent, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.gameTable = table;
  }

  private loadEvent(id: number) {
    this.eventService.getEventById(id).subscribe({
      next: (response) => {
        if (!response.success || !response.data) {
          this.handleMissing();
          return;
        }
        const loadedEvent = response.data;
        if (!this.permissionService.canViewDisabledEvent(loadedEvent)) {
          this.handleMissing();
          return;
        }
        this.event.set(loadedEvent);
        this.loadOrganizationWebsite(loadedEvent);
      },
      error: () => {
        this.handleMissing();
      }
    });
  }

  private handleMissing() {
    this.alertService.showError(Messages.EVENT_LOAD_ERROR);
    this.router.navigate(['/events']);
  }

  protected toggleAddInvitedOrganization(): void {
    this.showAddInvitedOrganization.update(v => !v);
    if (this.showAddInvitedOrganization()) {
      this.fetchActiveOrganizations();
      this.searchOrganizationControl.setValue(null);
    }
  }

  protected onAddInvitedOrganization(): void {
    const org = this.searchOrganizationControl.value;
    const ev = this.event();
    if (!ev?.id || !org) return;

    this.eventService.addInvitedOrganization(ev.id, org.id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.event.set(response.data);
          this.showAddInvitedOrganization.set(false);
          this.alertService.showSuccess(Messages.EVENT_INVITED_ORG_ADD_SUCCESS);
        }
      },
      error: () => {
        this.alertService.showError(Messages.EVENT_INVITED_ORG_ADD_ERROR);
      }
    });
  }

  protected async onRemoveInvitedOrganization(orgId: number, orgName: string): Promise<void> {
    const ev = this.event();
    if (!ev?.id) return;

    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_REMOVE,
      message: Messages.confirmInvitedOrganizationRemove(orgName)
    });
    if (!accepted) return;

    this.eventService.removeInvitedOrganization(ev.id, orgId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.event.set(response.data);
          this.alertService.showSuccess(Messages.EVENT_INVITED_ORG_REMOVE_SUCCESS);
        }
      },
      error: () => this.alertService.showError(Messages.EVENT_INVITED_ORG_REMOVE_ERROR)
    });
  }

  protected organizerName(event?: Event): string {
    return event?.organization?.organizationName || 'Organizzazione da definire';
  }

  protected organizerMeta(event?: Event): string {
    return '';
  }

  protected sanitizedDescription(value: unknown): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, String(value ?? '')) ?? '';
  }

  private loadOrganizationWebsite(event: Event): void {
    const organizationId = event.organization?.id;
    if (!organizationId) {
      this.organizationWebsite.set(undefined);
      return;
    }

    this.organizationService.getOrganizationById(organizationId).subscribe({
      next: (response) => {
        const website = response.data?.organizationWebsite?.trim();
        this.organizationWebsite.set(website || undefined);
      },
      error: () => {
        this.organizationWebsite.set(undefined);
      },
    });
  }

}

